import { ExtendedShareholderDTO } from "../types/Shareholder";

/**
 * Check if given nominal share value can be split into given amount
 *
 * ```
 * 11.0, 2 ===>>> new nominal 5.5           === OK
 * 4.5555, 2 ===>>> new nominal 2.27775     === NO, > 4 decimals
 * 11.0, 3 ===>>> new nominal 3.66666...    === NO
 * ```
 *
 * @param nominal number
 * @param splitInto integer
 * @returns boolean
 */
export const isValidSplit = (nominal: number, splitInto: number): boolean => {
  if (splitInto % 1 !== 0) {
    return false;
  }

  const multiplier = 10_000;

  return (nominal * multiplier) % splitInto === 0;
};

export const anyValidSplit = (nominal: number) => {
  const multiplier = 10_000;
  return nominal * multiplier > 1;
};

export const nextValidSplit = (nominal: number, splitInto: number) =>
  getValidSplitValues(nominal).find((value) => value > splitInto);

// with DCOM-193 (safety net)
export const isPossibleReverseSplit = (
  shareSumByType: { [shareTypeName: string]: number },
  reverseRatio: number,
  nominal: number,
): boolean => {
  const shareSums = Object.values(shareSumByType);
  return (
    reverseRatio % 1 === 0 &&
    shareSums.every((numberOfShares) => numberOfShares % reverseRatio === 0) &&
    // DCOM-193 (safety net)
    shareSums.reduce((sum, n) => sum + n * nominal, 0) ===
      shareSums.reduce((sum, n) => sum + (n / reverseRatio) * nominal * reverseRatio, 0)
  );
};

export const reversedWithoutFractions = (
  shareholders: ExtendedShareholderDTO[],
  reverseRatio: number,
): boolean => {
  return (
    reverseRatio % 1 === 0 &&
    shareholders.every((s) =>
      Object.values(s.calculatedShares).every(
        (numberOfShares) => numberOfShares % reverseRatio === 0,
      ),
    )
  );
};

// any valid reverse (with or without fractions)
export const closestReverse = (
  shareSumByType: { [shareTypeName: string]: number },
  reverseRatio: number,
) => {
  const validReversesByShareType = Object.values(shareSumByType)
    .filter((n) => n > 0)
    .map((numberOfShares) => getAllFactorNumbers(numberOfShares));
  const validReverses = validReversesByShareType
    .flat()
    .filter((n) => validReversesByShareType.every((reverses) => reverses.includes(n)))
    .sort((a, b) => a - b);

  return (
    validReverses.length &&
    validReverses.reduce((closest, v) =>
      Math.abs(reverseRatio - v) < Math.abs(reverseRatio - closest) ? v : closest,
    )
  );
};

export const closestReverseWithoutFractions = (
  shareholders: ExtendedShareholderDTO[],
  reverseRatio: number,
) => {
  const validReversesByShareType = shareholders.flatMap((s) =>
    Object.values(s.calculatedShares)
      .filter((n) => n > 0)
      .map((numberOfShares) => getAllFactorNumbers(numberOfShares)),
  );
  const validReverses = validReversesByShareType
    .flat()
    .filter((n) => validReversesByShareType.every((reverses) => reverses.includes(n)))
    .sort((a, b) => a - b);

  return (
    validReverses.length &&
    validReverses.reduce((closest, v) =>
      Math.abs(reverseRatio - v) < Math.abs(reverseRatio - closest) ? v : closest,
    )
  );
};

export const getValidSplitValues = (nominal: number) => {
  const multiplier = 10_000;
  return getAllFactorNumbers(Math.round(nominal * multiplier));
};

export const getAllFactorNumbers = (n: number) => {
  const factorNumbers = [n];
  for (let divisor = 2; divisor <= Math.floor(Math.sqrt(n)); divisor++) {
    if (n % divisor === 0) {
      factorNumbers.push(divisor);
      if (divisor !== n / divisor) factorNumbers.push(n / divisor);
    }
  }
  return factorNumbers.sort((a, b) => a - b);
};
